import React, { useRef } from 'react';
import { StyleSheet, TouchableHighlight, View } from "react-native";
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import Svg, { Rect, Path } from "react-native-svg"
// firebase
import callableFunctions from "../../firebase/functions/callable-functions";
// navigation
import { INTUIT_OAUTH_REDIRECT_PATH } from "../../config/navigation";
// utils
import { userAlert } from "../../utilities/alert";
import { useHover } from "react-native-web-hooks";

/**
 * Connect to QuickBooks button
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/go-live/list-on-the-app-store/naming-and-logo-guidelines
 *
 * @returns {JSX.Element}
 */
const C2QBBtn = ({ style }) => {
    const styles = useStyles();

    const ref = useRef(null);
    const isHovered = useHover(ref);

    // save auth uri in case the callable func takes too long to respond, we don't need to make the request again after
    // browser window fails to open.
    const authUriRef = useRef(null);

    const handlePress = async () => {

        const successUri = Linking.createURL(INTUIT_OAUTH_REDIRECT_PATH);

        if (!authUriRef.current) {
            try {
                const response = await callableFunctions.intuit.getAuthUri({
                    successUri
                });

                authUriRef.current = response.data.authUri;
            } catch (err) {
                userAlert("Failed to authenticate", "Please try again.");
                return;
            }

            if (!authUriRef.current) {
                userAlert("Failed to authenticate", "Please try again");
                return;
            }
        }

        /**
         * @type {WebBrowser.WebBrowserAuthSessionResult}
         */
        let result;
        try {
            result = await WebBrowser.openAuthSessionAsync(authUriRef.current, successUri);
        } catch (err) {
            return userAlert("Failed to authenticate", "The server took too long to respond, please try again.");
        }

        // delay allowing auth browser window to close.
        setTimeout(() => {
            if (result.type !== "success") {

                authUriRef.current = null;

                return userAlert("It looks like something isn't quite right", "Connection to QuickBooks may have failed, try again if it is not connected.");
            }

            userAlert("Quickbooks Connected!", "CREATE Bookings has successfully connected to Quickbooks.  You can now use all tenant management features.");
        }, 100);
    };

    // render
    return (
        <TouchableHighlight onPress={() => handlePress()} ref={ref} style={[ styles.touchable, style ]}>
            <View>
                <Svg
                    width={223}
                    height={36}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <Rect width={223} height={36} rx={4} fill="#2CA01C" />
                    {isHovered && (
                        <Rect width={223} height={36} rx={4} fill="#108000" fillOpacity={0.5} />
                    )}
                    <Path
                        d="M35.312 14.112c-.896-1.184-2.64-1.728-4.112-1.728-3.488 0-6.144 2.288-6.144 5.92 0 3.536 2.512 5.984 6.048 5.984 1.92 0 3.392-.736 4.384-1.984l-1.616-1.136c-.576.784-1.536 1.312-2.752 1.312-2.336 0-3.84-1.712-3.84-4.176 0-2.336 1.584-4.144 3.92-4.144.912 0 1.984.352 2.576 1.168l1.536-1.216Zm.697 6.016c0 2.416 1.856 4.096 4.176 4.096 2.32 0 4.176-1.68 4.176-4.096 0-2.416-1.856-4.032-4.176-4.032-2.32 0-4.176 1.616-4.176 4.032Zm1.952 0c0-1.2.704-2.464 2.224-2.464 1.52 0 2.224 1.264 2.224 2.464s-.704 2.496-2.224 2.496c-1.52 0-2.224-1.296-2.224-2.496ZM46.222 24h1.92v-4.224c0-1.024.576-2.08 1.792-2.08 1.232 0 1.44 1.136 1.44 2.048V24h1.92v-4.8c0-1.568-.832-3.104-2.832-3.104-1.136 0-2.048.688-2.384 1.456h-.032V16.32h-1.824V24Zm9.313 0h1.92v-4.224c0-1.024.576-2.08 1.792-2.08 1.232 0 1.44 1.136 1.44 2.048V24h1.92v-4.8c0-1.568-.832-3.104-2.832-3.104-1.136 0-2.048.688-2.384 1.456h-.032V16.32h-1.824V24Zm10.784-4.608c.08-1.072.992-1.856 2.128-1.856 1.248 0 1.808.864 1.808 1.856H66.32Zm5.856.864c0-2.768-1.568-4.16-3.712-4.16-2.32 0-4.064 1.632-4.064 4.112 0 2.496 1.76 4.016 4.128 4.016 1.456 0 2.544-.512 3.296-1.536l-1.28-1.024c-.432.608-1.072.992-2.032.992-1.12 0-2.112-.8-2.192-1.888h5.84c.016-.176.016-.336.016-.512Zm8.42-2.96c-.608-.752-1.776-1.2-2.896-1.2-2.256 0-4.112 1.568-4.112 4.064 0 2.56 1.888 4.064 4.16 4.064 1.136 0 2.192-.384 2.832-1.072l-1.2-1.216c-.368.448-1.024.688-1.616.688-1.456 0-2.176-1.264-2.176-2.464s.704-2.464 2.112-2.464c.624 0 1.28.32 1.584.752l1.312-1.152Zm1.492.56v3.584c0 1.472.464 2.72 2.496 2.72.432 0 .944-.08 1.28-.208l-.064-1.504c-.224.112-.56.16-.816.16-.848 0-1.008-.496-1.008-1.184v-3.568h1.92V16.32h-1.92v-2.224h-1.888v2.224h-1.344v1.536h1.344Zm9.438 0v3.584c0 1.472.464 2.72 2.496 2.72.432 0 .944-.08 1.28-.208l-.064-1.504c-.224.112-.56.16-.816.16-.848 0-1.008-.496-1.008-1.184v-3.568h1.92V16.32h-1.92v-2.224h-1.888v2.224H90.18v1.536h1.344Zm4.75 2.272c0 2.416 1.855 4.096 4.175 4.096s4.176-1.68 4.176-4.096c0-2.416-1.856-4.032-4.176-4.032-2.32 0-4.176 1.616-4.176 4.032Zm1.951 0c0-1.2.704-2.464 2.224-2.464 1.52 0 2.224 1.264 2.224 2.464s-.704 2.496-2.224 2.496c-1.52 0-2.224-1.296-2.224-2.496Zm24.566 2.24h-2.8v-.032c1.008-.72 2.016-2.112 2.016-4.144 0-3.44-2.544-5.808-6.016-5.808s-6.064 2.368-6.064 5.808c0 3.68 2.416 5.808 6.032 5.808h6.832v-1.632Zm-6.8-8.176c2.288 0 3.824 1.696 3.824 4 0 2.416-1.616 4.048-3.856 4.048-2.272 0-3.824-1.632-3.824-4.048 0-2.304 1.664-4 3.856-4Zm15.129 2.128h-1.92v4.224c0 1.024-.576 2.08-1.792 2.08-1.232 0-1.44-1.136-1.44-2.048V16.32h-1.92v4.8c0 1.568.832 3.104 2.832 3.104 1.136 0 2.048-.688 2.384-1.456h.032V24h1.824v-7.68Zm2.336 7.68h1.92v-7.68h-1.92V24Zm-.272-10.288c0 .608.512 1.152 1.216 1.152s1.248-.496 1.248-1.152c0-.656-.544-1.152-1.248-1.152s-1.216.544-1.216 1.152Zm11.082 3.584c-.608-.752-1.776-1.2-2.896-1.2-2.256 0-4.112 1.568-4.112 4.064 0 2.56 1.888 4.064 4.16 4.064 1.136 0 2.192-.384 2.832-1.072l-1.2-1.216c-.368.448-1.024.688-1.616.688-1.456 0-2.176-1.264-2.176-2.464s.704-2.464 2.112-2.464c.624 0 1.28.32 1.584.752l1.312-1.152ZM145.551 24h1.92v-3.936h.048l3.04 3.936h2.544l-3.536-4.208 3.328-3.472h-2.464l-2.912 3.232h-.048v-7.648h-1.92V24Zm8.752 0h4.08c2.144 0 4.288-.832 4.288-3.296 0-1.52-1.024-2.48-2.448-2.672V18c1.168-.352 1.968-1.2 1.968-2.48 0-2.064-1.76-2.848-3.472-2.848h-4.416V24Zm2.016-9.664h1.616c1.52 0 2.24.4 2.24 1.408 0 .976-.72 1.536-2.064 1.536h-1.792v-2.944Zm0 4.672h1.888c1.632 0 2.448.416 2.448 1.6 0 1.488-1.488 1.696-2.528 1.696h-1.808v-3.296Zm7.799 1.12c0 2.416 1.856 4.096 4.176 4.096 2.32 0 4.176-1.68 4.176-4.096 0-2.416-1.856-4.032-4.176-4.032-2.32 0-4.176 1.616-4.176 4.032Zm1.952 0c0-1.2.704-2.464 2.224-2.464 1.52 0 2.224 1.264 2.224 2.464s-.704 2.496-2.224 2.496c-1.52 0-2.224-1.296-2.224-2.496Zm7.814 0c0 2.416 1.856 4.096 4.176 4.096 2.32 0 4.176-1.68 4.176-4.096 0-2.416-1.856-4.032-4.176-4.032-2.32 0-4.176 1.616-4.176 4.032Zm1.952 0c0-1.2.704-2.464 2.224-2.464 1.52 0 2.224 1.264 2.224 2.464s-.704 2.496-2.224 2.496c-1.52 0-2.224-1.296-2.224-2.496ZM184.129 24h1.92v-3.936h.048l3.04 3.936h2.544l-3.536-4.208 3.328-3.472h-2.464l-2.912 3.232h-.048v-7.648h-1.92V24Zm14.018-6.736c-.624-.784-1.776-1.168-2.88-1.168-1.52 0-3.056.736-3.056 2.448 0 1.44 1.136 1.904 2.272 2.176 1.168.272 1.76.464 1.76 1.104 0 .688-.672.912-1.232.912a2.502 2.502 0 0 1-1.968-.96l-1.264 1.184c.736.816 1.968 1.264 3.152 1.264 1.68 0 3.2-.688 3.2-2.56 0-1.504-1.344-2.016-2.48-2.288-1.072-.256-1.664-.416-1.664-1.008 0-.608.624-.832 1.232-.832.704 0 1.344.384 1.68.832l1.248-1.104Z"
                        fill="#fff"
                    />
                </Svg>
            </View>
        </TouchableHighlight>
    );
};

const useStyles = () => {

    return StyleSheet.create({

        touchable: {

            alignSelf: 'flex-start'
        }
    });
}

export default C2QBBtn;
